import _ from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {isTracker} from '../../../../helpers/modelHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {DraftMessageModel} from '../../../../models/api/messageModel';
import {AppState} from '../../../../store/appState';
import {
  InboxesSelector,
  makeGetConversationTeamInboxes,
  OptionalConversationSelector,
  TeammateIdsSelector,
} from '../../../../store/data/conversations/conversationsSelectors';
import {getConversations} from '../../../../store/data/dataSelectors';
import {makeGetIsMessageVisuallyShared} from '../../../../store/data/messages/messagesSelectors';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {ShareButtonCoordinator, ShareMessage} from './shareButtonCoordinator';

/*
 * Props.
 */

export interface ShareButtonCoordinatorContainerProps {
  message: DraftMessageModel;
  shareMessage: ShareMessage;
}

/*
 * Redux.
 */

function getMessage(_state: AppState, props: ShareButtonCoordinatorContainerProps) {
  return props.message;
}

function makeGetConversation() {
  return createSelector(
    [getConversations, getMessage],
    (conversations, message) => conversations[message.conversationId],
  );
}

function makeGetParticipantIds(
  getConversation: OptionalConversationSelector<ShareButtonCoordinatorContainerProps>,
) {
  return createSelector([getConversation, getUser], (conversation, user) => {
    if (!conversation) {
      return [];
    }

    return _(conversation.trackers)
      .filter((t) => isTracker(t))
      .compact()
      .filter((t) => t.teammateId !== user.id)
      .map((t) => t.teammateId)
      .compact()
      .value();
  });
}

function makeGetShouldInviteTeammates(
  getConversationTeamInboxes: InboxesSelector<ShareButtonCoordinatorContainerProps>,
  getParticipantIds: TeammateIdsSelector<ShareButtonCoordinatorContainerProps>,
) {
  return createSelector(
    [getConversationTeamInboxes, getParticipantIds],
    (conversationTeamInboxes, participantIds) => {
      if (conversationTeamInboxes.length > 0) {
        // Don't offer to invite teammates if the conversation is in a team inbox.
        return false;
      }

      if (participantIds.length !== 0) {
        // Don't offer to invite teammates if the conversation already has participants.
        return false;
      }

      // Offer to invite teammates before sharing.
      return true;
    },
  );
}

const mapStateToProps = makeMapStateToProps<ShareButtonCoordinatorContainerProps>()(() => {
  const getConversation = makeGetConversation();
  const getParticipantIds = makeGetParticipantIds(getConversation);
  const getConversationTeamInboxes = makeGetConversationTeamInboxes(getConversation);

  return {
    participantIds: getParticipantIds,
    conversationTeamInboxes: getConversationTeamInboxes,
    shouldInviteTeammates: makeGetShouldInviteTeammates(getConversationTeamInboxes, getParticipantIds),
    isMessageShared: makeGetIsMessageVisuallyShared(getMessage),
  };
});

export const ShareButtonCoordinatorContainer = connect(mapStateToProps)(ShareButtonCoordinator);
